import { Injectable, Signal, signal } from '@angular/core';

export type StmToastTone = 'success' | 'error' | 'info';

export type StmToastOptions = {
    /** Omit for the five-second default. Use null for a persistent toast. */
    durationMs?: number | null;
};

export type StmToast = {
    readonly id: number;
    readonly tone: StmToastTone;
    readonly message: string;
    readonly title: string | null;
    readonly durationMs: number | null;
};

export const STM_TOAST_DEFAULT_DURATION_MS = 5_000;
export const STM_TOAST_EXTENDED_DURATION_MS = 1_000;

@Injectable({ providedIn: 'root' })
export class StmToastService {
    private readonly toastState = signal<readonly StmToast[]>([]);
    private nextId = 0;

    readonly toasts: Signal<readonly StmToast[]> = this.toastState.asReadonly();

    success(message: string | null | undefined, title?: string, options?: StmToastOptions): void {
        this.show('success', message, title, options);
    }

    error(message: string | null | undefined, title?: string, options?: StmToastOptions): void {
        this.show('error', message, title, options);
    }

    info(message: string | null | undefined, title?: string, options?: StmToastOptions): void {
        this.show('info', message, title, options);
    }

    dismiss(id: number): void {
        this.toastState.update((toasts) => toasts.filter((toast) => toast.id !== id));
    }

    private show(tone: StmToastTone, message: string | null | undefined, title?: string, options?: StmToastOptions): void {
        const durationMs = options?.durationMs === undefined ? STM_TOAST_DEFAULT_DURATION_MS : options.durationMs;
        const toast: StmToast = {
            id: ++this.nextId,
            tone,
            message: String(message ?? ''),
            title: title ? String(title) : null,
            durationMs
        };

        this.toastState.update((toasts) => [toast, ...toasts]);
    }
}
